import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { AlertTriangle } from "lucide-react";
import type { TagResponseDTO } from "../types";
import { memo } from "react";
import { TagBadge } from "./TagBadge";

interface TagDeleteDialogProps {
  open: boolean;
  loading: boolean;
  /** Tag selecionada para exclusão. `null` enquanto o dialog está fechado. */
  tag: TagResponseDTO | null;
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * Dialog de confirmação de exclusão de tag.
 *
 * Exibe o `TagBadge` e a descrição da tag alvo dentro de um bloco de alerta
 * destrutivo para que o usuário confirme a ação antes de prosseguir.
 *
 * @example
 * <TagDeleteDialog
 *   open={dialogMode === "delete"}
 *   loading={loading}
 *   tag={selectedTag}
 *   onClose={handleClose}
 *   onConfirm={handleDelete}
 * />
 */
export const TagDeleteDialog = memo(function TagDeleteDialog({
  open,
  loading,
  tag,
  onClose,
  onConfirm,
}: TagDeleteDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Excluir Tag</DialogTitle>
          <DialogDescription>
            Tem certeza que deseja excluir esta tag?
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <div className="p-4 bg-destructive/10 rounded-md border border-destructive/20">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
              <div className="space-y-1">
                {tag && <TagBadge tag={tag} />}
                {tag?.description && (
                  <p className="text-sm text-muted-foreground">
                    {tag.description}
                  </p>
                )}
                <p className="text-sm text-destructive mt-2">
                  Esta ação não pode ser desfeita!
                </p>
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={loading}>
            {loading ? "Excluindo..." : "Excluir Tag"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
